import React, { useEffect, useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import { useDispatch, useSelector } from 'react-redux';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as Sentry from '@sentry/react';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { setTransportationForm } from '../../../store/slices/transportation/transportationSlice';
import { Enum } from '../../../types/enum';
import { getDestinations } from '../../../apollo/queries/getDestinations';
import { getCarCompanies } from '../../../apollo/queries/getCarCompanies';
import { getTranslationByPath } from '../../../utils/translations';
import { isLoadingType, isUnloadingType } from '../../../utils/transportationType';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { checkAuthCode } from '../../../apollo/mutations/checkAuthCode';
import { isValidNumber } from '../../../utils/validators';

import './style.css'
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';
import { checkRegisteredTransportation } from '../../../apollo/mutations/checkRegisteredTransportation';
import { setSelectedPage } from '../../../store/slices/app/appSlice';

export type MainFormTemplateType = {
  transitionUrl: TransitionUrl
  registrationNumberInputLabel: TranslationPath
  registrationNumberInputPlaceholder: TranslationPath
  invalidRegistrationNumberText: TranslationPath
  driverNameInputLabel: TranslationPath
  driverNameInputPlaceholder: TranslationPath
  invalidDriverNameText: TranslationPath
  authCodeInputLabel: TranslationPath
  authCodeInputPlaceholder: TranslationPath
  invalidAuthCodeText: TranslationPath
  destinationInputLabel: TranslationPath
  destinationInputPlaceholder: TranslationPath
  carCompanyInputLabel: TranslationPath
  carCompanyInputPlaceholder: TranslationPath
  carQuantityInputLabel: TranslationPath
  carQuantityInputPlaceholder: TranslationPath
  voyageInputLabel: TranslationPath
  voyageInputPlaceholder: TranslationPath
  invalidVoyageText: TranslationPath
  registrationFailedText: TranslationPath
  transportationExistsText: TranslationPath
  invalidDestinationText: TranslationPath
  invalidCarCompanyText: TranslationPath
  invalidCarQuantityText: TranslationPath
}

type Props = {
  page: Page<MainFormTemplateType>
}

type SelectBoxProps = {
  value: string,
  setValue: (value: string) => void
  label: string,
  options: { id: string, name: string }[]
}

const SelectBox = (props: SelectBoxProps) => (
  <Select.Root value={props.value} onValueChange={props.setValue}>
    <Select.Trigger className="select">
      <Select.Value placeholder={props.label} />
      <Select.Icon className="select-icon">
        <ChevronDownIcon />
      </Select.Icon>
    </Select.Trigger>
    <Select.Portal>
      <Select.Content
        role="dialog"
        position="popper"
        className="popover"
        sideOffset={4}
      >
        <Select.Viewport className="SelectViewport">
          {props.options.map(option => (
            <Select.Item key={option.id} value={option.id} className="item">
              <Select.ItemText>{option.name}</Select.ItemText>
              <Select.ItemIndicator className="item-indicator">
                <CheckIcon />
              </Select.ItemIndicator>
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Portal>
  </Select.Root>
);

export const MainFormTemplate = (props: Props): React.ReactElement => {
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)
  const transportationTypeId = useSelector((state: RootState) => state.transportation.transportationTypeId)

  const language = useLanguage();
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [destinations, setDestinations] = useState<Enum[]>([]);
  const [carCompanies, setCarCompanies] = useState<Enum[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    const init = async () => {
      setDestinations((await getDestinations()).data.getDestinations)
      setCarCompanies((await getCarCompanies()).data.getCarCompanies)
      setIsLoading(false)
    }
    init()
  }, []);
  
  const processChecks = async () => {
    let data;
    if (fetchedTransportation === null) {
      if (!transportationTypeId) {
        return
      }

      data = {
        authCode: transportationForm.authCode,
        phoneNumber: transportationForm.phoneNumber,
      }
    } else {
      data = {
        authCode: transportationForm.authCode,
        transportationId: fetchedTransportation.id,
      }
    }

    try {
      setIsSaving(true)
      const registeredTransportation = await checkRegisteredTransportation({ registrationNumber: transportationForm.registrationNumber })
      if (!registeredTransportation.data?.checkRegisteredTransportation) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.transportationExistsText) },
        ))
        setIsSaving(false)
        return
      }
      const result = await checkAuthCode(data)
      if (result.data?.checkAuthCode) {
        dispatch(setSelectedPage(props.page.data.transitionUrl))
      } else {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
        ))
      }
    } catch (e) {
      console.log(e)
      dispatch( addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
      ))
      Sentry.captureException(e, { tags: { type: 'checkAuthCode' } })
    }
    setIsSaving(false)
  }

  const onContinue = () => {
    if (transportationForm.authCode.length === 0) {
      dispatch( addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidAuthCodeText) },
      ))
      return
    }

    if (isLoadingType(transportationTypeId) && (transportationForm.voyage === null || transportationForm.voyage.length === 0)) {
      dispatch( addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidVoyageText) },
      ))
      return
    }

    if (isUnloadingType(transportationTypeId)) {
      if (!isValidNumber(transportationForm.destinationId)) {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidDestinationText) },
        ))
        return
      }
      if (!isValidNumber(transportationForm.carCompanyId)) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidCarCompanyText) },
        ))
        return
      }
      if (!isValidNumber(transportationForm.carQuantity)) {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidCarQuantityText) },
        ))
        return
      }
    }

    if (fetchedTransportation === null) {
      if (transportationForm.registrationNumber.length === 0) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidRegistrationNumberText) },
        ))
        return
      }
      if (transportationForm.driverName.length === 0) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidDriverNameText) },
        ))
        return
      }
    }

    processChecks()
  }

  if (isLoading) {
    return <></>
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.registrationNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.registrationNumberInputLabel)}>
                <TextField.Root
                  disabled={fetchedTransportation !== null}
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.registrationNumberInputPlaceholder)}
                  value={transportationForm.registrationNumber}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      registrationNumber: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.driverNameInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.driverNameInputLabel)}>
                <TextField.Root
                  disabled={fetchedTransportation !== null}
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.driverNameInputPlaceholder)}
                  value={transportationForm.driverName}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      driverName: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.authCodeInputLabel)}
              </Label.Root>
              <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.authCodeInputLabel)}>
                <TextField.Root
                  type="number"
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.authCodeInputPlaceholder)}
                  value={transportationForm.authCode}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      authCode: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            {isUnloadingType(transportationTypeId) && (
              <>
                <Box maxWidth="500px" className="w-full mt-5">
                  <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                    {getTranslationByPath(language.translations, props.page.data.carCompanyInputLabel)}
                  </Label.Root>
                  {fetchedTransportation === null && (
                    <SelectBox
                      options={carCompanies.map(cc => ({ ...cc, id: cc.id.toString() }))}
                      value={transportationForm.carCompanyId ? transportationForm.carCompanyId.toString() : ''}
                      setValue={(e) => {
                        dispatch(setTransportationForm({
                          ...transportationForm,
                          carCompanyId: parseInt(e, 10),
                        }))
                      }}
                      label={getTranslationByPath(language.translations, props.page.data.carCompanyInputPlaceholder)}
                    />
                  )}
                  {fetchedTransportation !== null && (
                    <TextField.Root
                      disabled
                      style={{ height: '50px' }}
                      className="mt-3"
                      value={fetchedTransportation.carCompany}
                    />
                  )}
                </Box>

                <Box maxWidth="500px" className="w-full mt-5">
                  <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                    {getTranslationByPath(language.translations, props.page.data.destinationInputLabel)}
                  </Label.Root>
                  {fetchedTransportation === null && (
                    <SelectBox
                      options={destinations.map(d => ({ ...d, id: d.id.toString() }))}
                      value={transportationForm.destinationId ? transportationForm.destinationId.toString() : ''}
                      setValue={(e) => {
                        dispatch(setTransportationForm({
                          ...transportationForm,
                          destinationId: parseInt(e, 10),
                        }))
                      }}
                      label={getTranslationByPath(language.translations, props.page.data.destinationInputPlaceholder)}
                    />
                  )}
                  {fetchedTransportation !== null && (
                    <TextField.Root
                      disabled
                      style={{ height: '50px' }}
                      className="mt-3"
                      value={fetchedTransportation.destination}
                    />
                  )}
                </Box>

                <Box maxWidth="500px" className="w-full mt-5">
                  <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                    {getTranslationByPath(language.translations, props.page.data.carQuantityInputLabel)}
                  </Label.Root>
                  <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.carQuantityInputLabel)}>
                    <TextField.Root
                      disabled={fetchedTransportation !== null}
                      type="number"
                      style={{ height: '50px' }}
                      className="mt-3"
                      placeholder={getTranslationByPath(language.translations, props.page.data.carQuantityInputPlaceholder)}
                      value={transportationForm.carQuantity}
                      onChange={(e) => {
                        dispatch(setTransportationForm({
                          ...transportationForm,
                          carQuantity: e.target.value,
                        }))
                      }}
                    />
                  </KeyboardWrapper>
                </Box>
              </>
            )}

            {isLoadingType(transportationTypeId) && (
              <>
                <Box maxWidth="500px" className="w-full mt-5">
                  <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                    {getTranslationByPath(language.translations, props.page.data.voyageInputLabel)}
                  </Label.Root>
                  <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.voyageInputLabel)}>
                    <TextField.Root
                      style={{ height: '50px' }}
                      className="mt-3"
                      placeholder={getTranslationByPath(language.translations, props.page.data.voyageInputPlaceholder)}
                      value={transportationForm.voyage}
                      onChange={(e) => {
                        dispatch(setTransportationForm({
                          ...transportationForm,
                          voyage: e.target.value,
                        }))
                      }}
                    />
                  </KeyboardWrapper>
                </Box>
              </>
            )}

          </Flex>
        </Flex>
      </MainContent>

      <Footer backUrl={props.page.footer?.back} onContinue={onContinue} disabledContinueBtn={isSaving} />
    </>
  )
}
